import React from "react";
import { Helmet } from "react-helmet-async";

const SITE = "https://afrimarket.org";
const DEFAULT_OG = `${SITE}/og-image.png`;

/**
 * Reusable SEO component for per-page meta tags.
 * Drop into the top of any page component.
 *
 * Props:
 *   title:        Page title (will be suffixed with " · AfriMarket")
 *   description:  Meta description (160 chars max)
 *   path:         Route path e.g. "/browse" — used for canonical URL
 *   image:        Absolute URL of OG image (optional, defaults to og-image.png)
 *   type:         OG type: "website" | "article" | "product" (default "website")
 *   noindex:      true → prevents crawler indexing (use for auth/dashboard)
 *   jsonLd:       Object or array of JSON-LD structured data
 *   children:     Extra <meta>/<link> tags
 */
export const SEO = ({
  title,
  description,
  path = "/",
  image,
  type = "website",
  noindex = false,
  jsonLd,
  children,
}) => {
  const fullTitle = title ? `${title} · AfriMarket Group` : "AfriMarket Group — The Official African Diaspora Marketplace | UK · US · Canada";
  const url = `${SITE}${path}`;
  const ogImage = image || DEFAULT_OG;
  const desc =
    description ||
    "AfriMarket Group — the official marketplace for the African diaspora. Authentic products and trusted local services across the UK, US and Canada.";

  const ldArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Hreflang for UK/US/Canada English variants */}
      <link rel="alternate" hrefLang="en-gb" href={url} />
      <link rel="alternate" hrefLang="en-us" href={url} />
      <link rel="alternate" hrefLang="en-ca" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="AfriMarket Group" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={fullTitle} />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:site" content="@afrimarket" />

      {ldArray.map((obj, i) => (
        <script key={`ld-${i}`} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}

      {children}
    </Helmet>
  );
};

export default SEO;
